/**
 * LLM Enhancement Service
 * 
 * This service provides LLM functionality without interfering with existing components.
 * It works by intercepting and enhancing responses when LLM is available,
 * while maintaining complete separation from the CopilotPane component.
 */

import { AI, AuthProvider } from '@m365-core-design/sandbox';

export interface LLMEnhancementConfig {
  isEnabled: boolean;
  isAuthenticated: boolean;
  appType?: string;
}

export class LLMEnhancementService {
  private static instance: LLMEnhancementService;
  private config: LLMEnhancementConfig = {
    isEnabled: false,
    isAuthenticated: false,
  };
  private aiInstance: AI | null = null;
  private readonly STORAGE_KEY = 'microsoft-llm-state';
  private responseCache = new Map<string, { response: string; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {
    // Load persisted state on initialization
    this.loadPersistedState();
    // Initialize AI instance when authentication is available
    this.initializeAI();
    // Check actual authentication status periodically
    this.startAuthStatusCheck();
  }

  private loadPersistedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsedState = JSON.parse(stored);
        this.config = {
          ...this.config,
          ...parsedState,
        };
      }
    } catch (error) {
      console.warn('Failed to load persisted LLM state:', error);
    }
  }

  private savePersistedState(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.config));
    } catch (error) {
      console.warn('Failed to save LLM state:', error);
    }
  }

  private startAuthStatusCheck(): void {
    // Check authentication status every 30 seconds
    setInterval(async () => {
      if (this.config.isEnabled) {
        await this.verifyAuthenticationStatus();
      }
    }, 30000);
  }

  private async verifyAuthenticationStatus(): Promise<void> {
    try {
      if (this.aiInstance) {
        // Check if MSAL is properly initialized first
        try {
          const isLoggedIn = await AuthProvider.shared.isLoggedIn();
          if (this.config.isAuthenticated !== isLoggedIn) {
            this.config.isAuthenticated = isLoggedIn;
            this.savePersistedState();
          }
        } catch (authError) {
          console.warn('MSAL authentication check failed:', authError);
          // If there's any authentication error (like MSAL not initialized), mark as not authenticated
          if (this.config.isAuthenticated) {
            this.config.isAuthenticated = false;
            this.savePersistedState();
          }
        }
      }
    } catch (error) {
      console.warn('Failed to verify authentication status:', error);
      // If verification fails, assume not authenticated
      if (this.config.isAuthenticated) {
        this.config.isAuthenticated = false;
        this.savePersistedState();
      }
    }
  }

  private initializeAI(): void {
    try {
      // Create AI instance with AuthProvider
      this.aiInstance = new AI(AuthProvider.shared);
    } catch (error) {
      console.warn('Failed to initialize AI instance:', error);
      this.aiInstance = null;
    }
  }

  /**
   * Ensure MSAL is properly initialized before making API calls
   */
  private async ensureMSALInitialized(): Promise<boolean> {
    try {
      // Try to check authentication status - this will fail if MSAL isn't initialized
      await AuthProvider.shared.isLoggedIn();
      return true;
    } catch (error) {
      console.warn('MSAL not initialized, attempting to reinitialize...', error);
      
      try {
        // The authentication might need to be triggered through the UI
        // For now, just mark as not authenticated and let the user re-authenticate
        this.updateConfig({ isAuthenticated: false });
        return false;
      } catch (initError) {
        console.error('Failed to initialize MSAL:', initError);
        return false;
      }
    }
  }

  static getInstance(): LLMEnhancementService {
    if (!LLMEnhancementService.instance) {
      LLMEnhancementService.instance = new LLMEnhancementService();
    }
    return LLMEnhancementService.instance;
  }

  updateConfig(config: Partial<LLMEnhancementConfig>): void {
    this.config = { ...this.config, ...config };
    this.savePersistedState();
  }

  /**
   * Clear persisted authentication state when user signs out
   */
  clearAuthenticationState(): void {
    this.config.isAuthenticated = false;
    this.config.isEnabled = false;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear persisted LLM state:', error);
    }
  }

  getConfig(): LLMEnhancementConfig {
    return { ...this.config };
  }

  /**
   * Generate a fresh response to user query (not enhancing existing content)
   * @param userQuery The user's query
   * @returns Generated response
   */
  async generateResponse(userQuery: string): Promise<string> {
    if (!this.config.isEnabled || !this.config.isAuthenticated || !this.aiInstance) {
      throw new Error('LLM not available');
    }

    // Check cache first for faster responses
    const cacheKey = this.getCacheKey(userQuery);
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('Using cached response for query');
      return cached;
    }

    // Ensure MSAL is properly initialized
    const msalReady = await this.ensureMSALInitialized();
    if (!msalReady) {
      throw new Error('MSAL not ready');
    }

    try {
      // Check if user has access to Substrate LLM
      console.log('Checking LLM access...');
      const accessResult = await this.aiInstance.substrateLLMHasAccess();
      console.log('LLM access result:', accessResult);
      
      if (!accessResult.hasAccess) {
        console.warn('User does not have access to Substrate LLM');
        throw new Error('User does not have access to Substrate LLM');
      }

      // Determine optimal token limit based on query complexity
      const maxTokens = this.calculateOptimalTokens(userQuery);
      
      // Select optimal model based on query type
      const model = this.selectOptimalModel(userQuery);

      console.log('Making LLM request with:', { model, maxTokens, userQuery });

      // Prepare the chat completion request for generating a fresh response
      const chatRequest = {
        model: model,
        promptArgs: {
          messages: [
            {
              role: 'system' as const,
              content: 'You are a helpful AI assistant integrated into Microsoft Office. Answer user questions directly and accurately. Provide clear, informative, and practical responses. If asked about factual information, provide accurate facts. If asked for help with documents, provide relevant assistance.'
            },
            {
              role: 'user' as const,
              content: userQuery
            }
          ],
          max_tokens: maxTokens,
          temperature: 0.3 // Lower temperature for more focused responses
          // Removed stream parameter to avoid API issues
        }
      };

      // Call the Substrate LLM API with timeout for faster failure
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('LLM request timeout')), 15000); // 15 second timeout
      });

      console.log('Calling substrateLLMChatCompletions...');
      const responsePromise = this.aiInstance.substrateLLMChatCompletions(chatRequest);
      const response = await Promise.race([responsePromise, timeoutPromise]);
      
      console.log('LLM API response received:', response);
      
      if (response?.choices?.[0]?.message?.content) {
        const content = response.choices[0].message.content.trim();
        console.log('Extracted content from LLM:', content);
        
        // Cache the response for future use
        this.addToCache(cacheKey, content);
        
        return content;
      } else {
        console.error('No content in LLM response:', response);
        throw new Error('No content received from LLM API');
      }
    } catch (error) {
      console.error('Error in generateResponse:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Check for MSAL initialization errors
      if (error instanceof Error && error.message.includes('uninitialized_public_client_application')) {
        console.warn('MSAL not initialized, marking as unauthenticated');
        this.updateConfig({ isAuthenticated: false });
      }
      
      throw error; // Re-throw to let caller handle
    }
  }

  /**
   * Enhance a response with LLM capabilities if available
   * @param originalResponse The original response text
   * @param userQuery The user's query
   * @param onChunk Optional callback for streaming responses
   * @returns Enhanced response or original response
   */
  async enhanceResponse(
    originalResponse: string, 
    userQuery?: string,
    onChunk?: (chunk: string) => void
  ): Promise<string> {
    if (!this.config.isEnabled || !this.config.isAuthenticated || !this.aiInstance) {
      return originalResponse;
    }

    // Check cache first for faster responses
    const cacheKey = this.getCacheKey(userQuery || '');
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      console.log('Using cached response for query');
      if (onChunk) {
        // Stream cached response for consistent UX
        await this.simulateStreaming(cached, onChunk);
      }
      return cached;
    }

    // Ensure MSAL is properly initialized
    const msalReady = await this.ensureMSALInitialized();
    if (!msalReady) {
      console.warn('MSAL not ready, falling back to original response');
      return originalResponse;
    }

    try {
      // Check if user has access to Substrate LLM
      const accessResult = await this.aiInstance.substrateLLMHasAccess();
      if (!accessResult.hasAccess) {
        console.warn('User does not have access to Substrate LLM');
        return originalResponse;
      }

      // Determine optimal token limit based on query complexity
      const maxTokens = this.calculateOptimalTokens(userQuery || '');
      
      // Select optimal model based on query type
      const model = this.selectOptimalModel(userQuery || '');

      // Prepare the optimized chat completion request
      const chatRequest = {
        model: model,
        promptArgs: {
          messages: [
            {
              role: 'system' as const,
              content: 'You are a helpful AI assistant integrated into Microsoft Office. Provide concise, accurate, and helpful responses to user queries. Focus on being practical and actionable. Keep responses brief but informative.'
            },
            {
              role: 'user' as const,
              content: userQuery || 'Please provide a helpful response'
            }
          ],
          max_tokens: maxTokens,
          temperature: 0.3, // Lower temperature for faster, more focused responses
          stream: !!onChunk // Enable streaming if callback provided
        }
      };

      // Call the Substrate LLM API with timeout for faster failure
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('LLM request timeout')), 10000); // 10 second timeout
      });

      const responsePromise = this.aiInstance.substrateLLMChatCompletions(chatRequest);
      const response = await Promise.race([responsePromise, timeoutPromise]);
      
      if (response?.choices?.[0]?.message?.content) {
        const content = response.choices[0].message.content.trim();
        
        // Cache the response for future use
        this.addToCache(cacheKey, content);
        
        // If streaming callback provided, simulate streaming for better UX
        if (onChunk) {
          await this.simulateStreaming(content, onChunk);
        }
        
        return content;
      } else {
        console.warn('No content received from LLM API');
        return originalResponse;
      }
    } catch (error) {
      console.error('Error calling Substrate LLM API:', error);
      
      // Check for MSAL initialization errors
      if (error instanceof Error && error.message.includes('uninitialized_public_client_application')) {
        console.warn('MSAL not initialized, marking as unauthenticated');
        this.updateConfig({ isAuthenticated: false });
      }
      
      return originalResponse;
    }
  }

  /**
   * Generate cache key from user query
   */
  private getCacheKey(query: string): string {
    return query.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /**
   * Get response from cache if available and not expired
   */
  private getFromCache(key: string): string | null {
    const cached = this.responseCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.response;
    }
    if (cached) {
      // Remove expired cache entry
      this.responseCache.delete(key);
    }
    return null;
  }

  /**
   * Add response to cache
   */
  private addToCache(key: string, response: string): void {
    // Clean up old entries to prevent memory leaks
    if (this.responseCache.size > 100) {
      const oldestKey = this.responseCache.keys().next().value;
      if (oldestKey) {
        this.responseCache.delete(oldestKey);
      }
    }
    
    this.responseCache.set(key, {
      response,
      timestamp: Date.now()
    });
  }

  /**
   * Calculate optimal token limit based on query complexity
   */
  private calculateOptimalTokens(query: string): number {
    const baseTokens = 150; // Base for simple responses
    const queryLength = query.length;
    
    if (queryLength < 50) {
      return baseTokens; // Simple queries get shorter responses
    } else if (queryLength < 100) {
      return Math.min(250, baseTokens + queryLength); // Medium queries
    } else {
      return Math.min(400, baseTokens + Math.floor(queryLength * 1.5)); // Complex queries
    }
  }

  /**
   * Select optimal model based on query type
   */
  private selectOptimalModel(query: string): 'dev-gpt-4o-11-20' | 'dev-phi-3-mini-128k-instruct' {
    const simplePatterns = [
      /^(hi|hello|hey|what|how|when|where|who)/i,
      /^(can you|could you|please)/i,
      /\b(help|assist|support)\b/i
    ];
    
    const isSimpleQuery = simplePatterns.some(pattern => pattern.test(query)) && query.length < 100;
    
    // Use faster model for simple queries, more capable for complex ones
    return isSimpleQuery ? 'dev-phi-3-mini-128k-instruct' : 'dev-gpt-4o-11-20';
  }

  /**
   * Simulate streaming for better user experience
   */
  private async simulateStreaming(content: string, onChunk: (chunk: string) => void): Promise<void> {
    const words = content.split(' ');
    const chunkSize = Math.max(1, Math.floor(words.length / 10)); // Stream in ~10 chunks
    
    for (let i = 0; i < words.length; i += chunkSize) {
      const chunk = words.slice(i, i + chunkSize).join(' ');
      onChunk(chunk + (i + chunkSize < words.length ? ' ' : ''));
      
      // Faster streaming - reduced delay
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  /**
   * Check if LLM enhancement is available
   */
  isLLMAvailable(): boolean {
    return this.config.isEnabled && this.config.isAuthenticated;
  }

  /**
   * Generate an image using Microsoft AI image generation
   * @param prompt The text prompt for image generation
   * @param options Optional parameters for image generation
   * @returns Base64 encoded image data or null if generation fails
   */
  async generateImage(prompt: string, options?: {
    model?: 'dev-dall-e-3' | 'dev-gpt-4o-chatgpt-imagegen1p5';
    size?: '1024x1024' | '1536x1024' | '1024x1536';
    quality?: 'hd' | 'standard' | 'low' | 'medium' | 'high';
    style?: 'natural' | 'vivid';
  }): Promise<string | null> {
    console.log('=== generateImage called ===');
    console.log('Prompt:', prompt);
    console.log('Options:', options);
    console.log('Config:', this.config);
    console.log('AI Instance:', !!this.aiInstance);
    
    if (!this.config.isEnabled || !this.config.isAuthenticated || !this.aiInstance) {
      console.warn('LLM not available for image generation', {
        isEnabled: this.config.isEnabled,
        isAuthenticated: this.config.isAuthenticated,
        hasAIInstance: !!this.aiInstance
      });
      return null;
    }

    // Ensure MSAL is properly initialized
    const msalReady = await this.ensureMSALInitialized();
    if (!msalReady) {
      console.warn('MSAL not ready for image generation, falling back');
      return null;
    }

    try {
      // Check if user has access to Substrate LLM
      console.log('Checking LLM access...');
      const accessResult = await this.aiInstance.substrateLLMHasAccess();
      console.log('Access result:', accessResult);
      
      if (!accessResult.hasAccess) {
        console.warn('User does not have access to Substrate LLM for image generation');
        return null;
      }

      // Try DALL-E 3 with optimized settings for faster generation
      console.log('Generating image with DALL-E 3...');
      
      try {
        // Use optimized settings for faster generation
        const dalle3Request = {
          model: 'dev-dall-e-3' as const,
          promptArgs: {
            prompt: prompt.length > 1000 ? prompt.substring(0, 1000) : prompt,
            n: 1,
            size: '1024x1024' as const, // Standard size for faster generation
            quality: 'standard' as const, // Standard quality is faster than HD
            response_format: 'b64_json' as const,
            style: 'natural' as const
          }
        };
        
        console.log('DALL-E 3 request with optimized settings:', dalle3Request);
        const response = await this.aiInstance.substrateLLMImagesGenerations(dalle3Request);
        console.log('DALL-E 3 response received');
        
        if (response && 'data' in response && Array.isArray(response.data) && response.data.length > 0) {
          const imageData = response.data[0] as { b64_json?: string };
          if (imageData && 'b64_json' in imageData && imageData.b64_json) {
            console.log('DALL-E 3 image generated successfully');
            return imageData.b64_json;
          }
        }
      } catch (dalle3Error) {
        console.error('DALL-E 3 error:', dalle3Error);
        
        // Log specific error types for debugging
        if (dalle3Error instanceof Error) {
          if (dalle3Error.message.includes('404') || dalle3Error.message.includes('not found')) {
            console.error('DALL-E 3 API endpoint not found');
          } else if (dalle3Error.message.includes('403') || dalle3Error.message.includes('unauthorized')) {
            console.error('DALL-E 3 not authorized');
          } else if (dalle3Error.message.includes('timeout')) {
            console.error('DALL-E 3 timed out');
          }
        }
      }

      // Fallback to GPT-4o with optimized settings
      console.log('Falling back to GPT-4o for image generation...');
      try {
        const gpt4oRequest = {
          model: 'dev-gpt-4o-chatgpt-imagegen1p5' as const,
          promptArgs: {
            prompt: prompt.length > 1000 ? prompt.substring(0, 1000) : prompt,
            n: 1,
            size: '1024x1024' as const,
            quality: 'standard' as const,
            response_format: 'b64_json' as const
          }
        };
        
        console.log('GPT-4o request:', gpt4oRequest);
        const gpt4oResponse = await this.aiInstance.substrateLLMImagesGenerations(gpt4oRequest);
        console.log('GPT-4o response received');
        
        if (gpt4oResponse && 'data' in gpt4oResponse && Array.isArray(gpt4oResponse.data) && gpt4oResponse.data.length > 0) {
          const imageData = gpt4oResponse.data[0] as { b64_json?: string };
          if (imageData && 'b64_json' in imageData && imageData.b64_json) {
            console.log('GPT-4o image generated successfully');
            return imageData.b64_json;
          }
        }
      } catch (gpt4oError) {
        console.error('GPT-4o image generation also failed:', gpt4oError);
      }

      console.warn('Both DALL-E 3 and GPT-4o image generation failed');
      return null;
    } catch (error: unknown) {
      console.error('Error calling Substrate LLM Images API:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Check for MSAL initialization errors
      if (error instanceof Error && error.message.includes('uninitialized_public_client_application')) {
        console.warn('MSAL not initialized, marking as unauthenticated');
        this.updateConfig({ isAuthenticated: false });
      }
      
      return null;
    }
  }

  /**
   * Detect if a user query is requesting image generation
   * @param query The user's input text
   * @returns Boolean indicating if this is an image generation request
   */
  isImageGenerationRequest(query: string): boolean {
    const imageKeywords = [
      'generate an image', 'create an image', 'make an image', 'draw', 'paint',
      'generate a picture', 'create a picture', 'make a picture',
      'show me an image', 'can you draw', 'can you create',
      'image of', 'picture of', 'illustration of', 'artwork of',
      'visualize', 'sketch', 'render'
    ];
    
    const lowerQuery = query.toLowerCase().trim();
    return imageKeywords.some(keyword => lowerQuery.includes(keyword));
  }

  /**
   * Extract the image prompt from a user query
   * @param query The user's input text
   * @returns The extracted prompt for image generation
   */
  extractImagePrompt(query: string): string {
    // Common patterns to remove
    const patterns = [
      /^(can you |please |could you )?generate an? image (of |showing |with )?/i,
      /^(can you |please |could you )?create an? image (of |showing |with )?/i,
      /^(can you |please |could you )?make an? image (of |showing |with )?/i,
      /^(can you |please |could you )?draw (me )?an? image (of |showing |with )?/i,
      /^(can you |please |could you )?show me an? image (of |showing |with )?/i,
      /^(can you |please |could you )?(draw|paint|sketch|render|visualize) (me )?/i,
      /^image of /i,
      /^picture of /i,
      /^illustration of /i
    ];
    
    let prompt = query.trim();
    for (const pattern of patterns) {
      prompt = prompt.replace(pattern, '');
    }
    
    return prompt.trim() || query.trim();
  }

  /**
   * Check if the user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return this.config.isAuthenticated;
  }
}

export const llmService = LLMEnhancementService.getInstance();